import { Breadcrumbs, Typography, Link, Stack } from "@mui/material";
import { Link as LinkTo } from "react-router";
import { isValidId } from "../../../../../common/utils/utils";
import { LegendComponent } from "../../../../../common/components/legend_component";
import { LegendTagIcon } from "../../../../../common/components/legend_tag_icon";
import { LegendTagItemComponent } from "../../../../../common/components/legend_tag_item_component";
import { useMemo } from "react";
import { StyledDataGrid } from "../../../../../common/components/styled_data_grid";
import { ChangeHistoryModal } from "../../../../../common/modals/change_history_modal";
import { useEntregaObrasClienteVerificacaoViewModel } from "../../viewModels/entrega_obras_cliente_verificacao_view_model";
import { GenerateEntregaObraClienteTermoColumnsDefinition } from "./columns/entrega_obra_termo";
import { GenerateEntregaObraOcorrenciaColumnsDefinition } from "./columns/entrega_obra_ocorrencia";
import { GenerateVerificacaoChecklistItensColumnsDefinition } from "./columns/verificacao_checklist_itens";
import { PageTopBar, ThreeSlotLayout } from "../../../../../common/components/page_top_bar";
import { BackButton } from "../../../../../common/components/back_button";
import { CustomStyledDataGrid } from "./components/custom_styled_data_grid";
import { OcorrenciaModal } from "./components/ocorrencia_modal";
import { EntregaObraClienteTermoModel } from "../../../domain/models/entrega_obra_cliente_termo";
import { EntregaObraClienteArquivoAuxModel } from "../../../domain/models/entrega_obra_cliente_arquivo_aux";
import { GenerateEntregaObraArquivosAuxListaColumns } from "./columns/generateEntregaObraClientesArquivoAux";
import { EntregaObrasTopHeader } from "./components/entrega_obra_top_header";
import { InformacoesGeraisPanel } from "./components/informacoes_gerais_panel";
import { ListaItensChecklistForm } from "../../schemas/entrega_obra_cliente_verificacao/entregaObraClienteChecklistCadastro";
import { EntregaObraTermoModal } from "./components/entrega_obra_termo_modal";
import { EntregaObraClienteArquivoAuxModal } from "./components/entrega_obra_cliente_arquivo_aux_modal";
import { InformacoesEntregaObraClienteModel } from "../../../domain/models/informacoes_entrega_obra_cliente";
import { PesquisaSatisfacaoModal } from "./components/pesquisa_satisfacao_modal";
import { PesquisaSatisfacaoEntregaModal } from "./components/pesquisa_satisfacao_entrega_modal";
import { EntregaObraPesquisaSatisfacaoLista } from "../../../domain/models/entrega_obra_pesquisa_satisfacao_lista";
import { GenerateEntregaPesquisaSatisfacaoColumnDefinition } from "./columns/pesquisa_satisfacao";
import { ReportViewerModal } from "../../../../../common/components/report_viewer_modal";

import AlertError from "../../../../../common/components/alert_error";
import BottomBar from "../../../../../common/components/bottom_bar";
import PrimaryButton from "../../../../../common/components/primary_button";
import CascateList from "../../../../../common/components/cascate_list";
import FormContainer from "../../../../../common/components/form_container";
import DynamicIcon from "../../../../../common/components/dynamic_icon";
import DeleteDialog from "../../../../../common/components/delete_dialog";
import DefaultFormInput from "../../../../../common/components/default_form_input";
import { ImageViewerModal } from "../../../../../common/components/image_viewer_modal";

/**
 * EntregaObrasClienteVerificacao
 */

export const EntregaObrasClienteVerificacao = () => {
  const viewModel = useEntregaObrasClienteVerificacaoViewModel();

  const verificacaoObraItensColumnsDefinition = useMemo(() => {
    return GenerateVerificacaoChecklistItensColumnsDefinition({
      statusChecklist: viewModel.selectedEntregaObraClienteChecklist
        ? viewModel.selectedEntregaObraClienteChecklist.status : 0,
      lockChecklistItens: viewModel.lockInspecaoItens ?? false,
      onActionOcorrencia: viewModel.handleOcorrenciaModalOpenState,
      onActionInspecao1Change: viewModel.handleInspecao1Change,
      onActionInspecao2Change: viewModel.handleInspecao2Change,
      onActionLimpar: viewModel.handleClearInspecaoObraItem,
    });
  }, [
    viewModel.selectedEntregaObraClienteChecklist,
    viewModel.lockInspecaoItens,
    viewModel.handleClearInspecaoObraItem,
    viewModel.handleInspecao1Change,
    viewModel.handleInspecao2Change,
    viewModel.handleOcorrenciaModalOpenState,
  ]);

  const termoColumnsDefinition = useMemo(() => {
    return GenerateEntregaObraClienteTermoColumnsDefinition({
      onReportSubmit: viewModel.handleReportTermo,
      onActionDelete: viewModel.handleDeleteTermoModalOpenState,
    });
  }, [
    viewModel.handleDeleteTermoModalOpenState,
    viewModel.handleReportTermo,
  ]);

  const entregaObraOcorrenciaColumnsDefinition = useMemo(() => {
    return GenerateEntregaObraOcorrenciaColumnsDefinition({
      statusChecklistServico: viewModel.selectedEntregaObraClienteChecklist
        ? viewModel.selectedEntregaObraClienteChecklist.status : 0,
      onActionEditar: viewModel.handleOcorrenciaModalOpenState,
      onActionDownload: viewModel.handleDownloadArquivoOcorrencia,
      onActionDeletar: viewModel.handleDeleteOcorrenciaModalOpenState,
    });
  }, [
    viewModel.selectedEntregaObraClienteChecklist,
    viewModel.handleDownloadArquivoOcorrencia,
    viewModel.handleOcorrenciaModalOpenState,
    viewModel.handleDeleteOcorrenciaModalOpenState,
  ]);


  const entregaObraArquivosAuxListaColumnsDefinition = useMemo(() => {
    return GenerateEntregaObraArquivosAuxListaColumns({
      onActionEdit: viewModel.handleArquivoAuxModalOpenState,
      onActionDelete: viewModel.handleDeleteArquivoAuxOpenState,
      onActionDownload: viewModel.handleDownloadArquivoAux,
    });
  }, [
    viewModel.handleDeleteArquivoAuxOpenState,
    viewModel.handleDownloadArquivoAux,
    viewModel.handleArquivoAuxModalOpenState,
  ]);

  const pesquisaSatisfacaoColumnDefinition = useMemo(() => {
    return GenerateEntregaPesquisaSatisfacaoColumnDefinition({
      onActionEditar: viewModel.handlePesquisaSatisfacaoEditarOpenState,
      onActionDelete: viewModel.handlePesquisaSatisfacaoDeleteOpenState,
    });
  }, [
    viewModel.handlePesquisaSatisfacaoEditarOpenState,
    viewModel.handlePesquisaSatisfacaoDeleteOpenState,
  ]);

  return (
    <>
      <form onSubmit={viewModel.handleSubmit}>

        <PageTopBar>
          <ThreeSlotLayout
            leftSlot={
              <div className="flex items-center ml-6">
                <div className="flex items-center bg-white border border-gray-200 rounded-md shadow-sm overflow-hidden h-[34px]">
                  <div className="bg-gray-100 text-black text-[10px] font-bold uppercase tracking-wider px-3 h-full flex items-center border-r border-gray-200">
                    Centro de Custo
                  </div>
                  <div className="px-4 text-sm font-bold text-primary-green h-full flex items-center">
                    {viewModel.selectedEntregaObraClienteChecklist !== undefined
                      ? viewModel.selectedEntregaObraClienteChecklist.nomeCentroCusto ?? "-" : "-"}
                  </div>
                </div>
              </div>
            }

            rightSlot={
              <div className="flex flex-row w-full justify-end gap-2">
                <BackButton onClick={() => viewModel.handlePageBack()} />
                <div>
                  <Stack direction="row" spacing={2}>

                    <div className="w-[220px] ">
                      <PrimaryButton
                        buttonColor="secondary"
                        startIcon={<DynamicIcon nameIcon="AddRounded" size={25} />}
                        onClick={() => viewModel.handleReportSimple()}>
                        Relatório
                      </PrimaryButton>
                    </div>

                  </Stack>
                </div>
              </div>
            }
          />
        </PageTopBar>

        <div className="flex w-full gap-2 flex-col py-2 px-4 mb-20">
          <Breadcrumbs aria-label="breadcrumb" className="cursor-pointer">
            <Link underline="hover" color="inherit">
              Entrega de Obras
            </Link>

            <Link underline="hover" color="inherit" component={'span'}>
              <LinkTo to='/EntregaObras_lista'>Assistência e Entrega de Obras</LinkTo>
            </Link>

            <Link underline="hover" color="inherit" component={'span'}>
              <LinkTo to={`/EntregaObrasCliente_lista?id=${viewModel.selectedEntregaObraClienteChecklist}`}>Entrega por Cliente</LinkTo>
            </Link>

            <Typography className="text-primary-green">
              Verificação de Checklist por Cliente
            </Typography>
          </Breadcrumbs>

          <EntregaObrasTopHeader
            title="Verificação de Checklist por Cliente"
            nomeEntregaObra={viewModel.selectedEntregaObraClienteChecklist !== undefined
              ? viewModel.selectedEntregaObraClienteChecklist.nomeCentroCusto ?? "" : ""}

            nomeChecklist={viewModel.selectedEntregaObraClienteChecklist !== undefined
              ? viewModel.selectedEntregaObraClienteChecklist.descricaoChecklist ?? "" : ""}

            nomeCliente={viewModel.selectedEntregaObraClienteChecklist !== undefined
              ? viewModel.selectedEntregaObraClienteChecklist.nomeCliente ?? "" : ""}

            nomeUnidade={viewModel.selectedEntregaObraClienteChecklist !== undefined
              ? viewModel.selectedEntregaObraClienteChecklist.nomeUnidade ?? "" : ""}

            additionalHeader={
              (
                <>
                  {/* <div className="flex flex-row gap-8">
                    <div className="flex flex-row text-black dark:text-white items-center">
                      <span className="font-extrabold text-xl mr-2">Área: </span>
                      <span className="mx-1 font-base text-base">{viewModel.obraChecklistServicoHeader.descricaoArea}</span>
                    </div>
                    <div className="flex flex-row text-black dark:text-white items-center">
                      <span className="font-extrabold text-xl mr-2">Checklist: </span>
                      <span className="mx-1 font-base text-base">{viewModel.obraChecklistServicoHeader.descricaoServico}</span>
                    </div>
                  </div> */}
                </>

              )
            }
            onReload={() => { }}
          />

          {
            viewModel.errorMessage !== undefined
              ? (
                <div className="w-full">
                  <AlertError
                    messageErro={viewModel.errorMessage}
                    onTryAgain={() => { }}
                  />
                </div>
              ) : (
                <>

                  <InformacoesGeraisPanel
                    statusChecklistServico={viewModel.selectedEntregaObraClienteChecklist
                      ? viewModel.selectedEntregaObraClienteChecklist.status : 0}
                    lockFuncionarioInspecionado={viewModel.lockInspecaoItens ?? false}
                    lockFuncionarioReinspecao={viewModel.lockInspecaoItens ?? false}
                    hookForm={viewModel.hookForm}
                  />

                  <Stack spacing={0}>
                    <div className="w-full flex flex-row justify-between items-end">
                      <div className="flex flex-col flex-wrap">
                        <LegendComponent>
                          <LegendTagItemComponent
                            icon={<LegendTagIcon title='NA' bgColor="#89c4f4" />}
                            label='Não Se Aplica'
                          />

                          <LegendTagItemComponent
                            icon={<LegendTagIcon title='A' bgColor="#45b6af" />}
                            label='Aprovado'
                          />

                          <LegendTagItemComponent
                            icon={<LegendTagIcon title='R' bgColor="#f8cb00" />}
                            label='Retrabalho'
                          />

                          <LegendTagItemComponent
                            icon={<LegendTagIcon title='X' bgColor="#f3565d" />}
                            label='Reprovado'
                          />

                          <LegendTagItemComponent
                            icon={<LegendTagIcon title='RA' bgColor="#45b6af" />}
                            label='Retrabalho Aprovado'
                          />

                        </LegendComponent>
                      </div>
                    </div>

                    <CustomStyledDataGrid
                      tableName="obra_itens_checklist_area_verificacao_inspecoes_items"
                      disableColumnMenu={false}
                      getRowId={(row: ListaItensChecklistForm) => row.idVerificacaoCliente}
                      columns={verificacaoObraItensColumnsDefinition}
                      loading={viewModel.loading}
                      rows={viewModel.itensChecklistLista}
                      rowCount={viewModel.itensChecklistLista.length}
                      getRowHeight={() => 'auto'}
                      disableRowSelectionOnClick={true}
                      disableColumnSorting={false}
                      pageSizeOptions={[10, 25, 50, 100]}
                      paginationMode="server"
                      paginationModel={{
                        page: 1,
                        pageSize: 100,
                      }}
                      treeData
                      getTreeDataPath={(row) => row.path}
                      groupingColDef={{
                        headerName: 'Descrição',
                        flex: 1,
                        minWidth: 300,
                        hideDescendantCount: true,
                        valueFormatter: (value: any) =>
                          typeof value === 'string' ? value.replace(/^\(\d+\)\s*/, '') : value,
                      }}
                    />
                  </Stack>

                  {
                    <CascateList title="Ocorrência"
                      defaultOpen={false}
                      fontWeight="bold"
                    >
                      <div className="w-full flex flex-col justify-end items-end">
                        <div className="w-[120px] flex justify-end mb-4">
                          <PrimaryButton
                            buttonColor="primary-green"
                            size="small"
                            onClick={() => viewModel.handleOcorrenciaModalOpenState(null, null, true)}>
                            Nova Ocorrência
                          </PrimaryButton>
                        </div>

                        <StyledDataGrid
                          tableName="obra_itens_checklist_area_verificacao_ocorrencias"
                          disableColumnMenu={false}
                          getRowId={(row: InformacoesEntregaObraClienteModel) => row.idInformacoesEntregaObraCliente}
                          columns={entregaObraOcorrenciaColumnsDefinition}
                          loading={viewModel.loading}
                          rows={viewModel.ocorrenciasLista}
                          rowCount={viewModel.ocorrenciasLista.length}
                          disableRowSelectionOnClick={true}
                          disableColumnSorting={false}
                          pageSizeOptions={[10, 25, 50, 100]}
                          paginationMode="server"
                          paginationModel={{
                            page: 1,
                            pageSize: 100
                          }}
                          autoHeight={true}
                          getRowHeight={() => 'auto'}
                        />
                      </div>
                    </CascateList>
                  }


                  {
                    <CascateList
                      title="Termos"
                      defaultOpen={false}
                      fontWeight="bold"
                    >
                      <div className="w-full flex flex-col justify-end items-end">
                        <div className="w-[120px] flex justify-end mb-4">
                          <PrimaryButton
                            buttonColor="primary-green"
                            size="small"
                            onClick={() => viewModel.handleTermosModalOpenState(true)}>
                            Novo Termo
                          </PrimaryButton>
                        </div>
                        <StyledDataGrid
                          tableName="obra_itens_checklist_area_verificacao_termos"
                          disableColumnMenu={false}
                          getRowId={(row: EntregaObraClienteTermoModel) => row.idEntregaObraClienteTermo}
                          columns={termoColumnsDefinition}
                          loading={viewModel.loading}
                          rows={viewModel.termosLista}
                          rowCount={viewModel.termosLista.length}
                          disableRowSelectionOnClick={true}
                          disableColumnSorting={false}
                          pageSizeOptions={[10, 25, 50, 100]}
                          paginationMode="server"
                          paginationModel={{
                            page: 1,
                            pageSize: 100
                          }}
                          autoHeight={true}
                          getRowHeight={() => 'auto'}
                        />
                      </div>
                    </CascateList>
                  }

                  {
                    <CascateList
                      title="Pesquisa de Satisfação"
                      defaultOpen={false}
                      fontWeight="bold"
                    >
                      <div className="w-full flex flex-col gap-2 p-4 bg-light-mode-2 dark:bg-dark-mode-2 rounded border border-border-light-mode dark:border-border-dark-mode text-black dark:text-white">
                        <div className="w-full flex flex-col gap-4">
                          <div className="w-full flex flex-row justify-between items-center">
                            <span className="text-black dark:text-white font-bold text-lg">
                              Pesquisa de Satisfação
                            </span>
                            <div className="w-[8rem]">
                              <PrimaryButton
                                buttonColor="primary-green"
                                startIcon={<DynamicIcon nameIcon="AddRounded" size={25} className="text-white" />}
                                onMouseDown={e => {
                                  viewModel.handlePesquisaSatisfacaoModalOpenState(true);
                                  e.preventDefault();
                                }}>
                                Novo Item
                              </PrimaryButton>
                            </div>
                          </div>
                          <Stack spacing={0}>
                            <StyledDataGrid
                              tableName="entrega_obra_cliente_cadastro_pesquisa_satisfacao"
                              disableColumnMenu={false}
                              getRowId={(row: EntregaObraPesquisaSatisfacaoLista) => row.idEntregaPesquisa}
                              columns={pesquisaSatisfacaoColumnDefinition}
                              loading={viewModel.loading}
                              disableRowSelectionOnClick={true}
                              rows={viewModel.entregaPesquisaSatisfacaoLista}
                              rowCount={viewModel.entregaPesquisaSatisfacaoLista.length}
                              pageSizeOptions={[10, 25, 50, 100]}
                              paginationMode="server"
                              paginationModel={{
                                page: 1,
                                pageSize: 100,
                              }}
                              autoHeight={true}
                              getRowHeight={() => 'auto'}
                            />

                          </Stack>
                        </div>
                      </div>
                    </CascateList>
                  }
                  {
                    <CascateList
                      title="Arquivos Auxiliares"
                      defaultOpen={false}
                      fontWeight="bold"
                    >
                      <div className="w-full flex flex-col justify-end items-end">
                        <div className="w-[120px] flex justify-end mb-4">
                          <PrimaryButton
                            buttonColor="primary-green"
                            size="small"
                            onClick={() => viewModel.handleArquivoAuxModalOpenState(null, true)}>
                            Novo Arquivo
                          </PrimaryButton>
                        </div>
                        <StyledDataGrid
                          tableName="obra_itens_checklist_area_verificacao_arquivos_aux"
                          disableColumnMenu={false}
                          getRowId={(row: EntregaObraClienteArquivoAuxModel) => row.idEntregaObraClienteArquivoAux}
                          columns={entregaObraArquivosAuxListaColumnsDefinition}
                          loading={viewModel.loading}
                          rows={viewModel.arquivoAuxLista}
                          rowCount={viewModel.arquivoAuxLista.length}
                          disableRowSelectionOnClick={true}
                          disableColumnSorting={false}
                          pageSizeOptions={[10, 25, 50, 100]}
                          paginationMode="server"
                          paginationModel={{
                            page: 1,
                            pageSize: 100
                          }}
                          autoHeight={true}
                          getRowHeight={() => 'auto'}
                        />
                      </div>
                    </CascateList>
                  }

                  <CascateList
                    title="Análise da vistoria"
                    defaultOpen={false}
                    fontWeight="bold"
                  >
                    <div className="w-full flex flex-col">
                      <FormContainer title="" arrangement="fullRow" border={false}>
                        <>
                          <DefaultFormInput
                            name='analiseVistoria'
                            multiline={true}
                            rows={10}
                            readOnly={false}
                            reactHookForm={viewModel.hookForm}
                          />
                        </>
                      </FormContainer>

                    </div>
                  </CascateList>
                  <div className="w-full mb-12" >
                    <CascateList
                      title="Assinaturas"
                      defaultOpen={false}
                      fontWeight="bold"
                    >
                      <div className="w-full flex flex-col">
                        <FormContainer title="" arrangement="fullRow" border={false}>
                          <div className="w-full flex flex-row justify-around">
                            <div className="flex flex-col gap-2 items-center">
                              {
                                viewModel.selectedEntregaObraClienteChecklist && viewModel.selectedEntregaObraClienteChecklist.assinaturaCliente ? (
                                  <img
                                    src={`data:image/jpeg;base64,${viewModel.selectedEntregaObraClienteChecklist.assinaturaCliente}`}
                                    alt="Assinatura Cliente"
                                    className="block w-[250px] h-[100px] bg-white"
                                  />
                                ) : (
                                  <div
                                    className="block w-[250px] h-[100px] border border-[#C6C6C6] dark:border-border-dark-mode">
                                  </div>
                                )
                              }
                              <span className="text-black dark:text-white">Cliente</span>
                            </div>

                            <div className="flex flex-col gap-2 items-center">
                              {
                                viewModel.selectedEntregaObraClienteChecklist && viewModel.selectedEntregaObraClienteChecklist.assinaturaConstrutora ? (
                                  <img
                                    src={`data:image/jpeg;base64,${viewModel.selectedEntregaObraClienteChecklist.assinaturaConstrutora}`}
                                    alt="Assinatura Construtora"
                                    className="block w-[250px] h-[100px] bg-white"
                                  />
                                ) : (
                                  <div className="block w-[250px] h-[100px] border border-[#C6C6C6] dark:border-border-dark-mode rounded">
                                  </div>
                                )
                              }
                              <span className="text-black dark:text-white">Construtora</span>
                            </div>
                          </div>

                        </FormContainer>
                      </div>
                    </CascateList>
                  </div>
                </>
              )
          }
        </div >

        {/**
          * BottomBar
          */}

        <BottomBar>
          <div className="w-full h-full items-center justify-between flex p-4 gap-4">
            <div>
              {
                isValidId(viewModel.selectedEntregaObraClienteChecklist) &&
                <PrimaryButton
                  buttonColor="yellow"
                  onClick={() => viewModel.handleOpenChangeHistory(null, null)}>
                  <span className="text-xs">
                    Historico de alterações
                  </span>
                </PrimaryButton>
              }
            </div>

            <div className="flex flex-row gap-4">
              {
                (viewModel.selectedEntregaObraClienteChecklist) &&
                <div className="w-[8rem]">
                  {
                    viewModel.saving
                      ? (
                        <PrimaryButton>
                          Salvando...
                        </PrimaryButton>
                      ) : (
                        <PrimaryButton
                          type="submit"
                          startIcon={<DynamicIcon nameIcon="CheckRounded" size={25} className="text-white" />}>
                          Salvar
                        </PrimaryButton>
                      )
                  }
                </div>
              }
            </div>
          </div>
        </BottomBar>
      </form >

      {/**
        * Modals
        */}

      <OcorrenciaModal
        open={viewModel.ocorrenciaModal}
        saving={viewModel.saving}
        loading={viewModel.loading}
        inspecoesObraItens={viewModel.itensChecklistListaBase}
        selectedInspecaoObraItem={viewModel.selectedItemChecklist}
        selectedOcorrencia={viewModel.selectedOcorrencia}
        onClose={() => viewModel.handleOcorrenciaModalOpenState(null, null, false)}
        onDownload={viewModel.handleDownloadArquivoOcorrencia}
        onDeleteFile={viewModel.handleDeleteFileOcorrencia}
        onSubmit={viewModel.handleOcorrenciaSubmit}
      />

      <EntregaObraTermoModal
        open={viewModel.termoModal}
        saving={viewModel.saving}
        loading={viewModel.loading}
        ddlTermos={viewModel.ddlTermos}
        onClose={() => viewModel.handleTermosModalOpenState(false)}
        onSubmit={viewModel.handleTermosSubmit}

      />

      <EntregaObraClienteArquivoAuxModal
        open={viewModel.arquivoAuxModal}
        saving={viewModel.saving}
        selectedEntregaObraArquivoAux={viewModel.selectedArquivoAux ?? null}
        onClose={() => viewModel.handleArquivoAuxModalOpenState(null, false)}
        onSubmit={viewModel.handleSubmitNewArquivoAux}
        onOpenChangeHistory={viewModel.handleOpenChangeHistory}
        onDownload={viewModel.handleDownloadArquivoAux}
        idEntregaObraCliente={viewModel.selectedIdEntregaObraClienteChecklist ?? 0}
      />

      <PesquisaSatisfacaoModal
        open={viewModel.pesquisaSatisfacaoModal}
        saving={viewModel.saving}
        loading={viewModel.loading}
        pesquisaSatisfacaoDropdown={viewModel.ddlPesquisaSatisfacao}
        onClose={() => viewModel.handlePesquisaSatisfacaoModalOpenState(false)}
        onSubmit={viewModel.handlePesquisaSatisfacaoListaSubmit}
      />

      <PesquisaSatisfacaoEntregaModal
        open={viewModel.pesquisaSatisfacaoEditModal}
        saving={viewModel.saving}
        loading={viewModel.loading}
        selectedEntregaObraClienteChecklist={viewModel.selectedEntregaObraClienteChecklist ?? null}
        selectedPesquisaSatisfacaoEntrega={viewModel.entregaPesquisaSatisfacao ?? null}
        onClose={() => viewModel.handlePesquisaSatisfacaoEditarOpenState(null, false)}
        onSubmit={viewModel.handlePesquisaSatisfacaoSubmit}
      />

      <DeleteDialog
        open={viewModel.deleteOcorrenciaModal}
        onClose={() => viewModel.handleDeleteOcorrenciaModalOpenState(null, false)}
        title={`Você realmente deseja excluir este item?`}
        onDelete={viewModel.handleDeleteOcorrenciaSubmit}
      />

      <DeleteDialog
        open={viewModel.deleteTermoModal}
        onClose={() => viewModel.handleDeleteTermoModalOpenState(null, false)}
        title={`Você realmente deseja excluir este item?`}
        onDelete={() => viewModel.handleDeleteTermoSubmit()}
      />

      <DeleteDialog
        open={viewModel.deleteArquivoAuxModal}
        onClose={() => viewModel.handleDeleteArquivoAuxOpenState(null, false)}
        title={`Você realmente deseja excluir este item?`}
        onDelete={() => viewModel.handleSubmitDeleteArquivoAux()}
      />

      <DeleteDialog
        open={viewModel.pesquisaSatisfacaoDeleteModal}
        saving={viewModel.saving}
        onClose={() => viewModel.handlePesquisaSatisfacaoDeleteOpenState(null, false)}
        title={`Você realmente deseja excluir este item?`}
        onDelete={viewModel.handlePesquisaSatisfacaoDeleteSubmit}
      />

      {/**
        * Relatorios obras.
        */}

      <ReportViewerModal
        reportViewerState={viewModel.reportViewerState}
      />

      <ImageViewerModal
        open={viewModel.imageViewerModalOpen}
        saving={viewModel.saving}
        nomeArquivo={viewModel.selectedImageFileName}
        arquivo={viewModel.selectedArquivoData}
        cancelButtonLabel="Fechar"
        savingLabel="Baixando..."
        buttonColor="primary-green"
        onClose={() => viewModel.handleImageViewerModalOpenState(false)}
      />

      {/**
        * Modal historico de alterações.
        */}

      <ChangeHistoryModal
        changeHistoryHandler={viewModel.changeHistory}
      />

    </>
  );
}